import { ControlMessage } from '../controlMessage/ControlMessage';
import { StreamClientLike } from '../streaming/StreamClientLike';

type BroadcastClient = StreamClientLike & {
	onDisconnected? : () => void;
	destroy? : () => void;
};

export class BroadcastService {
	private activeClients = new Map<number, BroadcastClient>();
	private cloudPhoneInstances : BroadcastClient[] = [];
	private static instance : BroadcastService;

	private constructor() {
	}

	static getInstance() : BroadcastService {
		if (!BroadcastService.instance) {
			BroadcastService.instance = new BroadcastService();
		}
		return BroadcastService.instance;
	}

	registerClient(client : BroadcastClient) : void {
		if (!client || !this.getClientUdid(client) || typeof client.sendMessage !== 'function') {
			console.error('[BroadcastService] 注册失败：无效的流客户端实例', {
				hasClient: !!client,
				udid: client && this.getClientUdid(client),
				hasSendMessage: client && typeof (client as any).sendMessage === 'function',
				mode: (client as any)?.mode,
				phoneId: (client as any)?.phoneId,
			});
			return;
		}
		this.cloudPhoneInstances.unshift(client);
		this.activeClients.set(client.phoneId, client);
		console.info('[BroadcastService] 注册成功', {
			udid: this.getClientUdid(client),
			mode: (client as any).mode,
			phoneId: client.phoneId,
			total: this.cloudPhoneInstances.length,
		});
	}

	unregisterClient() : void {
		this.cloudPhoneInstances.forEach((client) => {
			client.onDisconnected?.();
			client.destroy?.();
		});
		this.cloudPhoneInstances = [];
		this.activeClients.clear();
	}

	broadcastToAll(
		sourceClient : BroadcastClient,
		message : ControlMessage,
		excludeSelf : boolean = true,
	) : void {
		if (!sourceClient || !this.getClientUdid(sourceClient)) {
			console.error('广播失败：发送源客户端无效');
			return;
		}
		if (!(message instanceof ControlMessage)) {
			console.error('广播失败：消息必须是 ControlMessage 实例');
			return;
		}
		const sourceUdid = this.getClientUdid(sourceClient);
		this.cloudPhoneInstances.forEach((client) => {
			if (excludeSelf && this.getClientUdid(client) === sourceUdid) {
				return;
			}
			try {
				const peerUdid = this.getClientUdid(client);
				const peerMode = (client as any).mode;
				let extra : any = {};
				// scrcpy 客户端：检查 streamReceiver.ws 是否就绪
				const sr = (client as any).streamReceiver;
				if (sr && sr.ws) {
					extra.scrcpyWsState = sr.ws.readyState;  // 0 connecting,1 open,2 closing,3 closed
					extra.scrcpyQueued = (sr.events?.length) ?? 0;
				}
				// ws_room：检查 client.ws
				const wsroomWs = (client as any).ws;
				if (wsroomWs && wsroomWs.readyState !== undefined) {
					extra.wsRoomState = wsroomWs.readyState;
				}
				console.debug(
					`[BroadcastService] → ${peerMode}:${peerUdid} type=${(message as any)?.type}`,
					extra,
				);
				client.sendMessage(message);
			} catch (error) {
				console.warn('[BroadcastService] 单个 peer 转发失败:', error);
			}
		});
	}

	change_keyboard_ToAll(
		sourceClient : BroadcastClient,
		checked : boolean = true,
		excludeSelf : boolean = true,
	) : void {
		const sourceUdid = this.getClientUdid(sourceClient);
		this.cloudPhoneInstances.forEach((client) => {
			if (excludeSelf && this.getClientUdid(client) === sourceUdid) {
				return;
			}
			try {
				client.setHandleKeyboardEvents(checked);
			} catch (_error) {
				// Ignore individual client failures and continue updating peers.
			}
		});
	}

	getActiveClients() : Map<number, BroadcastClient> {
		return new Map(this.activeClients);
	}

	getAllClients() : BroadcastClient[] {
		return [...this.cloudPhoneInstances];
	}

	private getClientUdid(client : BroadcastClient) : string | undefined {
		return (client.player as any)?.udid;
	}
}
